import React, { useEffect, useState } from 'react';
import { Tabs, List, Avatar, Badge, Typography } from 'antd';
import { FireOutlined, CalendarOutlined, GiftOutlined, HeartOutlined } from '@ant-design/icons';
import Confetti from 'react-confetti';

const { Text } = Typography;

// Helper function to format date as "Month Day"
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Helper function to extract first name
const getFirstName = (fullName) => {
  if (!fullName) return '';
  // Remove titles (Mr., Ms., etc.) and suffixes (e.g., (STPL-0001))
  const cleanedName = fullName.replace(/^(Mr\.|Ms\.|Mrs\.)\s*/, '').replace(/\s*\([^)]+\)$/, '');
  const nameParts = cleanedName.trim().split(' ');
  // Return the first part as the first name
  return nameParts[0] || '';
};

// CSS styles for BirthdayBar
const styles = `
  /* Load Dancing Script font */
  @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap');

  .birthday-card-container {
    perspective: 1000px;
    width: 100%;
    height: 60px; /* Adjust height to fit content */
  }

  .birthday-card {
    position: relative;
    width: 100%;
    height: 100%;
    transition: transform 0.6s;
    transform-style: preserve-3d;
  }

  .birthday-card:hover {
    transform: rotateY(180deg);
  }

  .birthday-card-front,
  .birthday-card-back {
    position: absolute;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    display: flex;
    align-items: center;
    padding: 8px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    box-sizing: border-box;
  }

  .birthday-card-front {
    background: linear-gradient(to right, #fff0f5, #e6f3ff);
  }

  .birthday-card-back {
    background: linear-gradient(to right, #ffccc7, #ff4d4f);
    transform: rotateY(180deg);
    justify-content: center;
    align-items: center;
    color: white;
    font-family: 'Dancing Script', cursive;
    font-size: 16px;
    font-weight: 700;
    text-align: center;
    box-shadow: 0 0 10px rgba(255, 77, 79, 0.5);
  }

  .upcoming-card {
    transition: transform 0.3s ease;
    width: 100%;
    height: 60px;
    display: flex;
    align-items: center;
    padding: 8px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    background: linear-gradient(to right, #e6f3ff, #f3e7f7);
  }

  .upcoming-card:hover {
    transform: scale(1.05);
  }

  .birthday-glow {
    animation: glow 1.5s ease-in-out infinite;
    box-shadow: 0 0 10px rgba(255, 77, 79, 0.5), 0 0 20px rgba(255, 77, 79, 0.3);
  }

  .bounce-in {
    animation: bounce 0.5s ease;
  }

  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 10px rgba(255, 77, 79, 0.5), 0 0 20px rgba(255, 77, 79, 0.3);
    }
    50% {
      box-shadow: 0 0 20px rgba(255, 77, 79, 0.8), 0 0 30px rgba(255, 77, 79, 0.5);
    }
  }

  @keyframes bounce {
    0% { transform: translateY(-10px); }
    50% { transform: translateY(5px); }
    100% { transform: translateY(0); }
  }

  .balloon-badge {
    position: absolute;
    top: -10px;
    right: -10px;
    background: #ff4d4f;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    animation: float 2s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }
`;

// Inject styles only once
if (!document.getElementById('birthday-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'birthday-styles';
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

const BirthdayBar = ({ birthdays }) => {
  const { TodayBirthAnniversary = [], UpcommingBirthAnniversary = [] } = birthdays || {};
  
  // State to control confetti animation and default active tab
  const [showConfetti, setShowConfetti] = useState(false);
  const [defaultActiveKey, setDefaultActiveKey] = useState("today");
  
  // Get today's date in "Month Day" format
  const today = formatDate(new Date());

  // Function to check if tab has data
  const hasData = (tabKey) => {
    switch (tabKey) {
      case "today":
        return TodayBirthAnniversary && TodayBirthAnniversary.length > 0;
      case "upcoming":
        return UpcommingBirthAnniversary && UpcommingBirthAnniversary.length > 0;
      default:
        return false;
    }
  };

  // Effect to determine the default active tab based on available data
  useEffect(() => {
    // Check for data in priority order: today first, then upcoming
    if (hasData("today")) {
      setDefaultActiveKey("today");
    } else if (hasData("upcoming")) {
      setDefaultActiveKey("upcoming");
    } else {
      // Default to "today" if no data is available
      setDefaultActiveKey("today");
    }
  }, [TodayBirthAnniversary, UpcommingBirthAnniversary]);

  // Check if there are birthdays today
  useEffect(() => {
    const hasBirthdayToday = TodayBirthAnniversary.some(
      (person) => formatDate(person.BirthAnniversary) === today
    );
    if (hasBirthdayToday) {
      setShowConfetti(true);
      // Stop confetti after 6 seconds
      const timer = setTimeout(() => setShowConfetti(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [TodayBirthAnniversary, today]);

  const items = [
    {
      key: 'today',
      label: (
        <div className="flex items-center gap-1">
          <FireOutlined style={{ color: '#ff4d4f' }} />
          <Text strong style={{ fontSize: '14px', color: '#1f1f1f' }}>
            Today
          </Text>
          <Badge
            count={TodayBirthAnniversary.length}
            style={{ backgroundColor: '#ff4d4f', fontSize: '10px', height: '16px', lineHeight: '16px' }}
            overflowCount={99}
          />
        </div>
      ),
      children: (
        <div className="p-2 relative">
          {/* Confetti effect for today's birthdays */}
          {showConfetti && (
            <Confetti
              width={window.innerWidth}
              height={window.innerHeight}
              numberOfPieces={150}
              recycle={false}
              gravity={0.2}
              colors={['#ff4d4f', '#1890ff', '#eb2f96', '#fadb14', '#52c41a']}
              wind={0.02}
            />
          )}
          <List
            grid={{ gutter: 8, column: 2, xs: 1, sm: 2 }}
            dataSource={TodayBirthAnniversary}
            locale={{ emptyText: 'No birthdays today 🎉' }}
            renderItem={(person) => (
              <List.Item>
                <div className="birthday-card-container">
                  <div
                    className={`birthday-card ${
                      formatDate(person.BirthAnniversary) === today ? 'birthday-glow bounce-in' : ''
                    }`}
                  >
                    <div className="birthday-card-front">
                      <Avatar
                        size={32}
                        style={{ backgroundColor: '#1890ff', marginRight: '8px' }}
                      >
                        {person.EmployeeName?.charAt(0)}
                      </Avatar>
                      <div>
                        <Text strong style={{ fontSize: '12px', color: '#1f1f1f' }} className="truncate">
                          {person.EmployeeName}
                        </Text>
                        <div className="flex items-center gap-1">
                          <GiftOutlined style={{ color: '#eb2f96', fontSize: '12px' }} />
                          <Text type="secondary" style={{ fontSize: '10px' }}>
                            {formatDate(person.BirthAnniversary)}
                          </Text>
                        </div>
                      </div>
                      {formatDate(person.BirthAnniversary) === today && (
                        <span className="balloon-badge">🎈</span>
                      )}
                    </div>
                    <div className="birthday-card-back">
                      Happy Birthday {getFirstName(person.EmployeeName)}!
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      ),
    },
    {
      key: 'upcoming',
      label: (
        <div className="flex items-center gap-1">
          <CalendarOutlined style={{ color: '#1890ff' }} />
          <Text strong style={{ fontSize: '14px', color: '#1f1f1f' }}>
            Upcoming
          </Text>
          <Badge
            count={UpcommingBirthAnniversary.length}
            style={{ backgroundColor: '#1890ff', fontSize: '10px', height: '16px', lineHeight: '16px' }}
            overflowCount={99}
          />
        </div>
      ),
      children: (
        <div className="p-2">
          <List
            grid={{ gutter: 8, column: 2, xs: 1, sm: 2 }}
            dataSource={UpcommingBirthAnniversary}
            locale={{ emptyText: 'No upcoming birthdays 🎂' }}
            renderItem={(person) => (
              <List.Item>
                <div className="upcoming-card">
                  <Avatar
                    size={32}
                    style={{ backgroundColor: '#722ed1', marginRight: '8px' }}
                  >
                    {person.EmployeeName?.charAt(0)}
                  </Avatar>
                  <div>
                    <Text strong style={{ fontSize: '12px', color: '#1f1f1f' }} className="truncate">
                      {person.EmployeeName}
                    </Text>
                    <div className="flex items-center gap-1">
                      <CalendarOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
                      <Text type="secondary" style={{ fontSize: '10px' }}>
                        {formatDate(person.BirthAnniversary)}
                      </Text>
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      ),
    },
  ];

  return (
    <div
      className="birthday-bar rounded-lg shadow-lg" style={{ border: '1px solid var(--bg-column)' }}>
      <div className="flex items-center justify-center gap-0 p-2 bg-gradient-to-r from-purple-50 to-pink-50 rounded-t-lg">
        <HeartOutlined style={{ color: '#eb2f96', fontSize: '16px' }} />
        <Text strong style={{ color: '#1f1f1f', fontSize: '14px' }}>
          Birthday Celebrations
        </Text>
      </div>
      <Tabs
        defaultActiveKey={defaultActiveKey}
        key={defaultActiveKey}
        items={items}
        tabBarStyle={{ margin: 0, padding: '0 12px', background: '#fafafa' }}
        animated={{ inkBar: true, tabPane: false }}
      />
    </div>
  );
};

export default BirthdayBar;